import {
  createEntityAdapter,
  EntityAdapter,
  EntityState,
} from '@ngrx/entity';
import { createFeature, createReducer, createSelector, MemoizedSelector, on } from '@ngrx/store';

import { ProjectAddonModel } from '../../models/project-addon.model';
import { ProjectAddonActions } from './project-addon.actions';

export interface ProjectAddonState
  extends EntityState<ProjectAddonModel.API.AddonTypeResponse> {
  isLoading: boolean;
  planAddonState: ProjectTargetAddonState;
  cameraAddonState: ProjectTargetAddonState;
}

export interface ProjectTargetAddonState
  extends EntityState<ProjectAddonModel.AddonDocument> {
  isLoading: boolean;
  isSaving: boolean;
}

export const projectAddonTypeAdapter: EntityAdapter<ProjectAddonModel.API.AddonTypeResponse> =
  createEntityAdapter<ProjectAddonModel.API.AddonTypeResponse>({
    selectId: (addonType) => addonType._id,
  });

export const projectPlanAddonAdapter: EntityAdapter<ProjectAddonModel.AddonDocument> =
  createEntityAdapter<ProjectAddonModel.AddonDocument>({
    selectId: (addon) => addon._id,
  });

export const projectCameraAddonAdapter: EntityAdapter<ProjectAddonModel.AddonDocument> =
  createEntityAdapter<ProjectAddonModel.AddonDocument>({
    selectId: (addon) => addon._id,
  });

const initialPlanAddonState: ProjectTargetAddonState =
  projectPlanAddonAdapter.getInitialState({
    isLoading: false,
    isSaving: false,
  });

const initialCameraAddonState: ProjectTargetAddonState =
  projectCameraAddonAdapter.getInitialState({
    isLoading: false,
    isSaving: false,
  });

const initialState: ProjectAddonState = projectAddonTypeAdapter.getInitialState({
  isLoading: false,
  planAddonState: initialPlanAddonState,
  cameraAddonState: initialCameraAddonState,
});

export const projectAddonStateReducer = createReducer(
  initialState,
  on(ProjectAddonActions.resetToInitialState, () => ({
    ...initialState,
  })),
  on(ProjectAddonActions.loadAddonTypes, (state) => ({
    ...state,
    isLoading: true,
  })),
  on(ProjectAddonActions.loadAddonTypesSuccess, (state, { addonTypes }) =>
    projectAddonTypeAdapter.setAll(addonTypes, {
      ...state,
      isLoading: false,
    }),
  ),
  on(ProjectAddonActions.loadAddonTypesFail, (state) => ({
    ...state,
    isLoading: false,
  })),
  on(ProjectAddonActions.loadProjectPlanAddons, (state) => ({
    ...state,
    planAddonState: {
      ...state.planAddonState,
      isLoading: true,
    },
  })),
  on(ProjectAddonActions.loadProjectPlanAddonsSuccess, (state, { addons }) => ({
    ...state,
    planAddonState: projectPlanAddonAdapter.setAll(
      addons.filter((addon) => addon.target === ProjectAddonModel.AddonTarget.Plan),
      {
        ...state.planAddonState,
        isLoading: false,
      },
    ),
  })),
  on(ProjectAddonActions.loadProjectPlanAddonsFail, (state) => ({
    ...state,
    planAddonState: {
      ...state.planAddonState,
      isLoading: false,
    },
  })),
  on(ProjectAddonActions.loadProjectCameraAddons, (state) => ({
    ...state,
    cameraAddonState: {
      ...state.cameraAddonState,
      isLoading: true,
    },
  })),
  on(ProjectAddonActions.loadProjectCameraAddonsSuccess, (state, { addons }) => ({
    ...state,
    cameraAddonState: projectCameraAddonAdapter.setAll(
      addons.filter((addon) => addon.target === ProjectAddonModel.AddonTarget.Camera),
      {
        ...state.cameraAddonState,
        isLoading: false,
      },
    ),
  })),
  on(ProjectAddonActions.loadProjectCameraAddonsFail, (state) => ({
    ...state,
    cameraAddonState: {
      ...state.cameraAddonState,
      isLoading: false,
    },
  })),
  on(ProjectAddonActions.createProjectAddon, (state, { target }) => {
    switch (target) {
      case ProjectAddonModel.AddonTarget.Camera:
        return {
          ...state,
          cameraAddonState: {
            ...state.cameraAddonState,
            isSaving: true,
          }
        }
      case ProjectAddonModel.AddonTarget.Plan:
        return {
          ...state,
          planAddonState: {
            ...state.planAddonState,
            isSaving: true,
          },
        }
    }
  }),
  on(ProjectAddonActions.createProjectAddonFail, (state, { target }) => {
    switch (target) {
      case ProjectAddonModel.AddonTarget.Camera:
        return {
          ...state,
          cameraAddonState: {
            ...state.cameraAddonState,
            isSaving: false,
          }
        }
      case ProjectAddonModel.AddonTarget.Plan:
        return {
          ...state,
          planAddonState: {
            ...state.planAddonState,
            isSaving: false,
          },
        }
    }
  }),
  on(ProjectAddonActions.createProjectAddonSuccess, (state, { addon }) => {
    switch (addon.target) {
      case ProjectAddonModel.AddonTarget.Camera:
        return {
          ...state,
          cameraAddonState: projectCameraAddonAdapter.addOne(
            addon,
            {
              ...state.cameraAddonState,
              isSaving: false,
            },
          ),
        };

      case ProjectAddonModel.AddonTarget.Plan:
        return {
          ...state,
          planAddonState: projectPlanAddonAdapter.addOne(
            addon,
            {
              ...state.planAddonState,
              isSaving: false,
            },
          ),
        };
    }
  }),
  on(ProjectAddonActions.updateProjectAddon, (state, { target }) => {
    switch (target) {
      case ProjectAddonModel.AddonTarget.Camera:
        return {
          ...state,
          cameraAddonState: {
            ...state.cameraAddonState,
            isSaving: true,
          },
        };

      case ProjectAddonModel.AddonTarget.Plan:
        return {
          ...state,
          planAddonState: {
            ...state.planAddonState,
            isSaving: true,
          },
        };
    }
  }),
  on(ProjectAddonActions.updateProjectAddonSuccess, (state, { addon }) => {
    switch (addon.target) {
      case ProjectAddonModel.AddonTarget.Camera:
        return {
          ...state,
          cameraAddonState: projectCameraAddonAdapter.updateOne(
            {
              id: addon._id,
              changes: addon,
            },
            {
              ...state.cameraAddonState,
            },
          ),
        };

      case ProjectAddonModel.AddonTarget.Plan:
        return {
          ...state,
          planAddonState: projectPlanAddonAdapter.updateOne(
            {
              id: addon._id,
              changes: addon,
            },
            {
              ...state.planAddonState,
            },
          ),
        };
    }
  }),
  on(ProjectAddonActions.updateProjectAddonFail, (state, { target }) => {
    switch (target) {
      case ProjectAddonModel.AddonTarget.Camera:
        return {
          ...state,
          cameraAddonState: {
            ...state.cameraAddonState,
            isSaving: false,
          },
        };

      case ProjectAddonModel.AddonTarget.Plan:
        return {
          ...state,
          planAddonState: {
            ...state.planAddonState,
            isSaving: false,
          },
        };
    }
  }),
  on(ProjectAddonActions.bulkCreateCameraAddons, (state) => {
    return {
      ...state,
      cameraAddonState: {
        ...state.cameraAddonState,
        isSaving: true,
      },
    };
  }),
  on(ProjectAddonActions.bulkCreateCameraAddonsSuccess, (state, { addons }) => {
    return {
      ...state,
      cameraAddonState: projectCameraAddonAdapter.addMany(
        addons,
        {
          ...state.cameraAddonState,
          isSaving: false,
        }),
    };
  }),
  on(ProjectAddonActions.bulkCreateCameraAddonsFail, (state) => {
    return {
      ...state,
      cameraAddonState: {
        ...state.cameraAddonState,
        isSaving: false,
      },
    };
  }),
  on(ProjectAddonActions.bulkUpdateCameraAddons, (state) => {
    return {
      ...state,
      cameraAddonState: {
        ...state.cameraAddonState,
        isSaving: true,
      },
    };
  }),
  on(ProjectAddonActions.bulkUpdateCameraAddonsSuccess, (state, { addons }) => {
    return {
      ...state,
      cameraAddonState: projectCameraAddonAdapter.upsertMany(
        addons,
        {
          ...state.cameraAddonState,
          isSaving: false,
        },
      ),
    };
  }),
  on(ProjectAddonActions.bulkUpdateCameraAddonsFail, (state) => {
    return {
      ...state,
      cameraAddonState: {
        ...state.cameraAddonState,
        isSaving: false,
      },
    };
  }),
  on(ProjectAddonActions.bulkUpdatePlanAddons, (state) => {
    return {
      ...state,
      planAddonState: {
        ...state.planAddonState,
        isSaving: true,
      },
    };
  }),
  on(ProjectAddonActions.bulkUpdatePlanAddonsSuccess, (state, { addons }) => {
    return {
      ...state,
      planAddonState: projectPlanAddonAdapter.upsertMany(
        addons,
        {
          ...state.planAddonState,
          isSaving: false,
        },
      ),
    };
  }),
  on(ProjectAddonActions.bulkUpdatePlanAddonsFail, (state) => {
    return {
      ...state,
      planAddonState: {
        ...state.planAddonState,
        isSaving: false,
      },
    };
  }),
  on(ProjectAddonActions.bulkDeleteCameraAddons, (state) => {
    return {
      ...state,
      cameraAddonState: {
        ...state.cameraAddonState,
        isSaving: true,
      },
    };
  }),
  on(ProjectAddonActions.bulkDeleteCameraAddonsSuccess, (state, { addonIds }) => {
    return {
      ...state,
      cameraAddonState: projectCameraAddonAdapter.removeMany(
        addonIds,
        {
          ...state.cameraAddonState,
          isSaving: false,
        },
      ),
    };
  }),
  on(ProjectAddonActions.bulkDeleteCameraAddonsFail, (state) => {
    return {
      ...state,
      cameraAddonState: {
        ...state.cameraAddonState,
        isSaving: false,
      },
    };
  }),
  on(ProjectAddonActions.bulkDeletePlanAddons, (state) => {
    return {
      ...state,
      planAddonState: {
        ...state.planAddonState,
        isSaving: true,
      },
    };
  }),
  on(ProjectAddonActions.bulkDeletePlanAddonsSuccess, (state, { addonIds }) => {
    return {
      ...state,
      planAddonState: projectPlanAddonAdapter.removeMany(
        addonIds,
        {
          ...state.planAddonState,
          isSaving: false,
        },
      ),
    };
  }),
  on(ProjectAddonActions.bulkDeletePlanAddonsFail, (state) => {
    return {
      ...state,
      planAddonState: {
        ...state.planAddonState,
        isSaving: false,
      },
    };
  }),
);

export const projectAddonFeature = createFeature({
  name: 'projectAddon',
  reducer: projectAddonStateReducer,
  extraSelectors: ({ selectProjectAddonState }) => {
    const { selectAll, selectEntities } =
      projectAddonTypeAdapter.getSelectors(selectProjectAddonState);
    const {
      selectAll: selectAllPlanAddons,
      selectEntities: selectPlanAddonEntities,
    } = projectPlanAddonAdapter.getSelectors(
      createSelector(
        selectProjectAddonState,
        (state) => state.planAddonState,
      ),
    );
    const {
      selectAll: selectAllCameraAddons,
      selectEntities: selectCameraAddonEntities,
    } = projectCameraAddonAdapter.getSelectors(
      createSelector(
        selectProjectAddonState,
        (state) => state.cameraAddonState,
      ),
    );

    const selectPlanAddonState = createSelector(
      selectProjectAddonState,
      (state) => state.planAddonState,
    );

    const selectCameraAddonState = createSelector(
      selectProjectAddonState,
      (state) => state.cameraAddonState,
    );

    const selectPlanAddonsIsLoading = createSelector(
      selectPlanAddonState,
      (state): boolean => state.isLoading,
    );

    const selectCameraAddonsIsLoading = createSelector(
      selectCameraAddonState,
      (state): boolean => state.isLoading,
    );

    const selectPlanAddonsIsSaving = createSelector(
      selectPlanAddonState,
      (state): boolean => state.isSaving,
    );

    const selectCameraAddonsIsSaving = createSelector(
      selectCameraAddonState,
      (state): boolean => state.isSaving,
    );

    const selectAddonTypesByTarget = (target: ProjectAddonModel.AddonTarget): MemoizedSelector<object, ProjectAddonModel.API.AddonTypeResponse[]> =>
      createSelector(selectAll, (addonTypes) =>
        addonTypes.filter((addonType) => addonType.target === target),
      );

    return {
      selectAddonTypes: selectAll,
      selectAddonTypeEntities: selectEntities,
      selectAddonTypesByTarget,
      selectProjectPlanAddons: selectAllPlanAddons,
      selectProjectPlanAddonEntities: selectPlanAddonEntities,
      selectProjectPlanAddonsIsLoading: selectPlanAddonsIsLoading,
      selectProjectCameraAddons: selectAllCameraAddons,
      selectProjectCameraAddonEntities: selectCameraAddonEntities,
      selectProjectCameraAddonsIsLoading: selectCameraAddonsIsLoading,
      selectProjectPlanAddonsIsSaving: selectPlanAddonsIsSaving,
      selectProjectCameraAddonsIsSaving: selectCameraAddonsIsSaving,
    };
  },
});
